import { formatDateInputValue } from "../../lib/dateTime";
import { useRecordClock } from "./recordClock";

const NEUTRAL = "var(--muted-foreground)";
const AMBER = "var(--signal-amber)";
const RED = "var(--destructive)";
const GREEN = "var(--signal-green)";
const INK = "var(--foreground)";

/** Same progress vocabulary as the web property panel; refusal is a solid ×. */
const STATUS: Record<string, { color: string; fraction?: number; end?: "check" | "cross"; dashed?: boolean; core?: boolean }> = {
  // The CORE is what separates a status someone CHOSE from the absence of one:
  // with no status at all this table misses and the fallback below draws the
  // bare dashed ring, which is exactly what Draft used to be.
  draft: { color: NEUTRAL, dashed: true, core: true },
  "not started": { color: NEUTRAL, dashed: true, core: true },
  open: { color: AMBER },
  pending: { color: AMBER, fraction: .25 },
  initiated: { color: AMBER, fraction: .25 },
  "in progress": { color: "var(--color-info)", fraction: .5 },
  "ready for review": { color: GREEN, fraction: .75 },
  "not accepted": { color: RED, end: "cross" },
  rejected: { color: RED, end: "cross" },
  voided: { color: NEUTRAL, end: "cross" },
  verified: { color: GREEN, end: "check" },
  approved: { color: GREEN, end: "check" },
  completed: { color: INK, end: "check" },
  closed: { color: INK, end: "check" },
};

export function StatusMark({ value }: { value: string }) {
  const key = value.trim().toLowerCase().replaceAll("_", " ");
  const s = STATUS[key] ?? { color: NEUTRAL, dashed: true };
  return (
    <svg data-status-mark={key || "unset"} width="16" height="16" viewBox="0 0 16 16"
      color={s.color} fill="none" aria-hidden style={{ flex: "none" }}>
      {s.end ? <>
        <circle cx="8" cy="8" r="7" fill="currentColor" />
        <path d={s.end === "check" ? "M4.5 8.2l2.2 2.3 4.8-5" : "M5.6 5.6l4.8 4.8M10.4 5.6l-4.8 4.8"}
          stroke="var(--card)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </> : <>
        <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.8"
          strokeDasharray={s.dashed ? "2 2.2" : undefined} />
        {s.core && <circle cx="8" cy="8" r="2.4" fill="currentColor" fillOpacity=".45" />}
        {!!s.fraction && <>
          <circle cx="8" cy="8" r="4" fill="currentColor" fillOpacity=".14" />
          <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="4"
            strokeDasharray={`${s.fraction * 4 * Math.PI} 100`} transform="rotate(-90 8 8)" />
        </>}
      </>}
    </svg>
  );
}

export function PriorityMark({ value }: { value: string }) {
  const key = value.trim().toLowerCase();
  // Urgent must still stand apart when the property is reduced to its icon.
  if (key === "urgent") return (
    <svg width="16" height="16" viewBox="0 0 16 16" color={RED}
      aria-hidden style={{ flex: "none" }}>
      <rect x="1" y="1" width="14" height="14" rx="3" fill="currentColor" />
      <path d="M8 4.2v4.5" stroke="var(--card)" strokeWidth="1.9" strokeLinecap="round" />
      <circle cx="8" cy="11.6" r="1" fill="var(--card)" />
    </svg>
  );
  const lit = ({ low: 1, medium: 2, high: 3 } as Record<string, number>)[key] ?? 0;
  const color = lit === 3 ? RED : lit === 2 ? AMBER : NEUTRAL;
  return (
    <svg data-meter-lit={lit} width="16" height="16" viewBox="0 0 16 16" color={color}
      aria-hidden style={{ flex: "none" }}>
      {[6, 9, 12].map((height, i) => <rect key={i} x={1 + i * 5} y={14 - height}
        width="3.5" height={height} rx="1" fill={i < lit ? "currentColor" : "var(--foreground)"}
        fillOpacity={i < lit ? 1 : .16} />)}
    </svg>
  );
}

/** Keep the year and calendar-day semantics; never parse a label like "Jan 5". */
export function propertyDateValue(raw: unknown): string | null {
  return raw instanceof Date || typeof raw === "string" || typeof raw === "number"
    ? formatDateInputValue(raw) : null;
}

export type DueTone = "none" | "overdue" | "soon" | "later";
export function dueTone(day: string | null | undefined, now: number): DueTone {
  if (!day || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return "none";
  const [year, month, date] = day.split("-").map(Number);
  const due = new Date(year!, month! - 1, date!);
  if (due.getFullYear() !== year || due.getMonth() !== month! - 1 || due.getDate() !== date) return "none";
  const today = new Date(now);
  // Calendar-day arithmetic stays correct through DST and December → January.
  const delta = (Date.UTC(year!, month! - 1, date!) - Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) / 86_400_000;
  return delta < 0 ? "overdue" : delta <= 7 ? "soon" : "later";
}

/**
 * An unset calendar is an EMPTY one: the header band and the day marks are
 * what a date fills in, so with no date neither is drawn and the glyph keeps
 * just its body and its rings.
 *
 * It needs a form of its own for two reasons. It needs SOMETHING, because a
 * date far in the future is drawn in this same muted ink with these same day
 * marks — so "no due date" and "due in March" were the same picture. And it
 * cannot be the dashed treatment every other blank mark gets
 * (`.record-property-unset`), because a calendar is five strokes and a filled
 * band: dashing all of them at 16px reads as damage, not as absence.
 */
export function CalendarMark(props: { dateValue?: string | null; due?: boolean; empty?: boolean }) {
  const now = useRecordClock();
  return <CalendarMarkAt {...props} now={now} />;
}

/**
 * The calendar mark against an explicit clock — no hook, so a surface that
 * renders the phone's marks with its own React (the office console) can draw
 * it. `CalendarMark` is this with the record clock.
 */
export function CalendarMarkAt({ dateValue, due = false, empty, now }: { dateValue?: string | null; due?: boolean; empty?: boolean; now: number }) {
  const blank = empty ?? !dateValue;
  const tone = due ? dueTone(dateValue, now) : "none";
  const color = tone === "overdue" ? RED : tone === "soon" ? "var(--property-due-soon)" : NEUTRAL;
  return (
    <svg data-due-tone={blank ? "unset" : tone} width="16" height="16" viewBox="0 0 18 18" color={color}
      fill="none" aria-hidden style={{ flex: "none" }}>
      <rect x="2" y="3.5" width="13" height="12" rx="2.5" stroke="currentColor" strokeWidth="1.7" />
      {blank
        ? <path d="M2.4 7.5h12.2" stroke="currentColor" strokeWidth="1.5" />
        : <path d="M2 6a2.5 2.5 0 012.5-2.5h8A2.5 2.5 0 0115 6v1.5H2z" fill="currentColor" />}
      <path d="M5.5 2v3M11.5 2v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      {blank ? null : tone === "overdue" ? <>
        <circle cx="13.4" cy="13.4" r="4.4" fill="var(--card)" />
        <circle cx="13.4" cy="13.4" r="3.5" fill="currentColor" />
        <path d="M12.1 12.1l2.6 2.6M14.7 12.1l-2.6 2.6" stroke="var(--card)" strokeWidth="1.3" strokeLinecap="round" />
      </> : <path d="M5.4 10.8h.1M8.5 10.8h.1M11.6 10.8h.1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />}
    </svg>
  );
}
